import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import type { FormName } from "@searchui-searchforms/shared/config";
import { FCI_ERROR } from "@searchui-searchforms/shared/fci";
import type { FormMetadataParams } from "@searchui-searchforms/shared/schemas";
import { logFCI } from "@/shared/lib/fci";
import { ErrorFallback } from "./ErrorFallback";

export interface RemoteErrorBoundaryProps {
    children: ReactNode;
    onError?: (error: unknown, errorInfo: ErrorInfo) => void;
    fallback?: ReactNode;
    formMetadataParams: FormMetadataParams;
    formName?: FormName;
}

interface State {
    hasError: boolean;
}

export class RemoteErrorBoundary extends Component<RemoteErrorBoundaryProps, State> {
    constructor(props: RemoteErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): State {
        return { hasError: true };
    }

    override componentDidCatch(error: unknown, errorInfo: ErrorInfo): void {
        this.props.onError?.(error, errorInfo);
        this.logError(error, errorInfo);
    }

    private logError(error: unknown, errorInfo: ErrorInfo) {
        logFCI({
            errorType: FCI_ERROR.Main,
            errorInfo: `RemoteErrorBoundary caught an error: ${error}`,
            details: {
                stack: error instanceof Error ? error.stack : undefined,
                componentStack: errorInfo.componentStack ?? undefined,
                formName: this.props.formName,
                ...this.props.formMetadataParams,
            },
        });
    }

    override render(): ReactNode {
        if (this.state.hasError) {
            return this.props.fallback ?? <ErrorFallback />;
        }
        return this.props.children;
    }
}
